import { useEffect, useMemo } from 'react';
import { unzipWith, sortBy, isEmpty } from 'lodash';
import { useSetAtom, useAtomValue } from 'jotai';
import { filterNAMetricsAtom } from '@src/atoms/page';
import { updateExportDataAtom } from '@components/export/atom';
import { getMetricMetaByKey } from '@src/utils/metrics';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';
import { percentileConfig } from '@utils/metrics/constants';

interface UseSetChartExportWithPercentileProps {
  pageKey: string;
  sectionKey: string;
  widgetKey: string;
  widgetInfo: any;
  data?: Record<string, any>;
  totals?: Record<string, any>;
  percentile?: string;
  percentileMetricsList?: string[];
}

/**
 * Custom hook for exporting chart data with percentile prefix support
 * Similar to useSetChartExport but adds percentile prefix to metric display names
 */
function useSetChartExportWithPercentile({
  pageKey,
  sectionKey,
  widgetKey,
  widgetInfo,
  data = {},
  totals = {},
  percentile = 'avg',
  percentileMetricsList = [],
}: UseSetChartExportWithPercentileProps) {
  const metricsToNA = useAtomValue(filterNAMetricsAtom);
  const { flowId, frustrationSignalId } = useDrilldownMeta();
  const updateExportData = useSetAtom(updateExportDataAtom);

  const { headers, flatData, footerData } = useMemo(() => {
    const headers: any = [{ key: 'timestamp', title: 'Timestamp', type: 'timestamp' }];
    const footerDataRow: any = !isEmpty(totals) ? ['Total'] : [];

    // Get percentile prefix if not 'avg'
    const prefix =
      percentile !== 'avg' && percentileConfig[percentile]
        ? `${percentileConfig[percentile].short} - `
        : '';

    const metrics = sortBy(
      Object.keys(data).map((m) =>
        getMetricMetaByKey({
          metricKey: m,
          flowOverride: !!flowId,
          frustrationSignalOverride: !!frustrationSignalId,
        }),
      ),
      ['displayName'],
    );
    let flatData: any = [];

    try {
      const metricsData = metrics.map(({ name: metric, displayName, unit }) => {
        // Check if this metric is in the percentile metrics list
        const isPercentileMetric = percentileMetricsList.includes(metric);

        // Add percentile prefix to display name if applicable
        const finalDisplayName =
          isPercentileMetric && prefix ? `${prefix}${displayName}` : displayName;

        headers.push({ key: metric, title: finalDisplayName + (unit === '' ? '' : ` (${unit})`) });
        footerDataRow.push(totals?.[metric]);
        if (metricsToNA.includes(metric)) {
          return data[metric].map((d: any) => [d.timestamp, 'NA']);
        }
        return data[metric];
      });
      // time + metrics
      flatData = unzipWith(metricsData, (...group) => {
        return [group[0][0], ...group.map((g) => g[1])];
      });
    } catch (err) {
      // pass
    }

    return { headers, flatData, footerData: [footerDataRow] };
  }, [data, totals, percentile, percentileMetricsList, metricsToNA, flowId, frustrationSignalId]);

  useEffect(() => {
    const path = ['sections', sectionKey, widgetKey];
    updateExportData({
      action: '$set',
      path,
      data: { widgetInfo, headers, data: flatData, footerData },
    });
    return () => {
      updateExportData({ action: '$unset', path, data: null });
    };
  }, [sectionKey, widgetKey, headers, flatData, footerData, widgetInfo, updateExportData]);
}

export default useSetChartExportWithPercentile;
